import { ChevronDown, Download, ExternalLink, Smartphone } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import {
  appStoreMetadata,
  type AppStoreMetadataResult,
} from 'sprites-types/workflow/tools/appStoreMetadata'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function AppStoreMetadataDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(appStoreMetadata, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const parsed = parseResult.data

  // Handle emitMode and nullable behavior (when failIfEmpty is false)
  const items: NonNullable<AppStoreMetadataResult>[] = Array.isArray(parsed)
    ? parsed.filter((item): item is NonNullable<AppStoreMetadataResult> => Boolean(item))
    : parsed
      ? [parsed as NonNullable<AppStoreMetadataResult>]
      : []

  // Header actions
  const headerActions: ContextAction[] = [
    {
      id: 'export-metadata',
      label: 'Export All',
      icon: <Download className="h-4 w-4" />,
      onClick: () => {
        console.log('Export App Store metadata:', items)
      },
    },
  ]

  return (
    <div className="space-y-3">
      {/* Header with context menu and collapse/expand */}
      <ContextMenuWrapper actions={headerActions}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Smartphone className="h-5 w-5 text-gray-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">App Store Metadata</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {items.length} items
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {/* Individual apps - show/hide based on expanded state */}
      {isExpanded && (
        <div className="space-y-2">
          {items.map((item) => {
            const contextActions: ContextAction[] = [
              {
                id: 'open-app-store',
                label: 'Open in App Store',
                icon: <ExternalLink className="h-4 w-4" />,
                onClick: () => {
                  const appStoreUrl = `https://apps.apple.com/app/id${item.trackId}`
                  window.open(appStoreUrl, '_blank')
                },
              },
            ]

            return (
              <ContextMenuWrapper key={item.trackId} actions={contextActions}>
                <div className="mx-8 rounded-3xl border border-gray-200/50 bg-white p-4 text-black shadow-sm backdrop-blur-[8px]">
                  <div className="flex gap-4">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={item.logo}
                      alt={`${item.sellerName} logo`}
                      className="h-12 w-12 flex-shrink-0 rounded-xl object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-gray-900">
                        {item.sellerName}
                      </div>
                      <div className="truncate text-xs text-gray-600">{item.primaryGenreName}</div>
                      <div className="mt-1 grid grid-cols-3 gap-3 text-xs text-gray-700">
                        <div>
                          <div className="font-medium">Rating</div>
                          <div>{item.averageUserRating?.toFixed?.(1) ?? 'N/A'}</div>
                        </div>
                        <div>
                          <div className="font-medium">Reviews</div>
                          <div>{item.userRatingCount ?? 0}</div>
                        </div>
                        <div>
                          <div className="font-medium">Version</div>
                          <div>{item.version}</div>
                        </div>
                      </div>
                      {item.description && (
                        <div className="mt-2 line-clamp-3 text-xs text-gray-700">
                          {item.description}
                        </div>
                      )}
                      {item.screenshotUrls?.length > 0 && (
                        <div className="mt-3 flex gap-2 overflow-x-auto">
                          {item.screenshotUrls.map((url) => (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              key={url}
                              src={url}
                              alt="screenshot"
                              className="h-20 rounded-lg object-cover"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </ContextMenuWrapper>
            )
          })}
        </div>
      )}
    </div>
  )
}

function downloadAppStoreMetadata(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function appStoreMetadataToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(appStoreMetadata, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const parsed = parseResult.data

  const items: NonNullable<AppStoreMetadataResult>[] = Array.isArray(parsed)
    ? parsed.filter((item): item is NonNullable<AppStoreMetadataResult> => Boolean(item))
    : parsed
      ? [parsed as NonNullable<AppStoreMetadataResult>]
      : []

  if (items.length === 0) {
    return `## App Store Metadata

No apps found.

---
`
  }

  const tableHeader = `| App | Genre | Rating | Reviews | Version | Link |
|-----|-------|---------|---------|---------|------|`

  const tableRows = items
    .map((item) => {
      const rating = item.averageUserRating?.toFixed?.(1) ?? 'N/A'
      const reviews = item.userRatingCount ?? 0
      const appStoreLink = `[View](https://apps.apple.com/app/id${item.trackId})`

      return `| ${item.sellerName} | ${item.primaryGenreName} | ${rating} | ${reviews} | ${item.version} | ${appStoreLink} |`
    })
    .join('\n')

  const descriptionsSection = items
    .filter((item) => item.description)
    .map((item) => `### ${item.sellerName}\n\n${item.description}`)
    .join('\n\n')

  return `## App Store Metadata (${items.length} apps)

${tableHeader}
${tableRows}

${descriptionsSection ? `\n## App Descriptions\n\n${descriptionsSection}\n` : ''}
---
`
}

export const AppStoreMetadataDisplay = createWorkflowToolComponent(
  AppStoreMetadataDisplayComponent,
  downloadAppStoreMetadata,
  appStoreMetadataToMarkdown
)
